<template>
  <div>
    <b-sidebar
      id="sidebar-consultant-certificate"
      sidebar-class="sidebar-lg"
      :visible="consultantCertificateSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="val => mutateConsultantCertificateSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">

        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Solicitação de certificado</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>

        <!-- Loading do formulário -->
        <div
          v-if="loading.form"
          class="justify-content-center align-items-center"
        >
          <b-spinner
            class="my-3 mx-auto d-block"
            variant="primary"
          />
        </div>

        <!-- Form -->
        <b-form
          v-else
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-row>
            <!-- PN -->
            <b-col cols="12">
              <b-form-group
                label="PN"
                label-for="certificate-consultant-pn"
                class="mt-1"
              >
              <v-select
                id="certificate-consultant-pn"
                v-model="consultant"
                :reduce="c => c.id"
                :options="consultants"
                :loading="loading.consultants"
                :disabled="loading.consultants || consultantDisabled"
                label="name"
                placeholder="Selecione o consultor"
                :class="getSelectErrorClass(v$.consultant.$error)"
                @input="v$.consultant.$touch()"
              />
                <div
                  class="invalid-feedback"
                  :class="{ 'd-block': v$.consultant.$error }"
                >
                  <span v-if="v$.consultant.required.$invalid">
                    Você deve informar um consultor.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Tipo de curso -->
            <b-col cols="12">
              <b-form-group
                label="Tipo de curso"
                label-for="certificate-type"
                class="mt-1"
              >
                <v-select
                  id="certificate-type"
                  v-model="certificateType"
                  :reduce="t => t.id"
                  :options="certificateTypeOptions"
                  :loading="loading.certificateTypes"
                  :disabled="loading.certificateTypes || isEditingCertificate"
                  label="name"
                  placeholder="Selecione o tipo de curso"
                  :class="getSelectErrorClass(v$.certificateType.$error)"
                  @input="v$.certificateType.$touch()"
                />
                <div
                  class="invalid-feedback"
                  :class="{ 'd-block': v$.certificateType.$error }"
                >
                  <span v-if="v$.certificateType.required.$invalid">
                    Você deve informar o tipo de curso.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex mt-2 justify-content-between">
            <template v-if="!isPendingApproval">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="outline-secondary"
                type="reset"
                class="certificate-buttons"
              >
                Cancelar
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="primary"
                type="submit"
                class="certificate-buttons"
              >
                {{ saving ? "Aguarde..." : "Salvar e enviar para análise" }}
              </b-button>
            </template>
            <template v-else>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="outline-danger"
                class="certificate-buttons"
                @click="reject"
              >
                {{ saving ? "Aguarde..." : "Rejeitar" }}
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="success"
                class="certificate-buttons"
                @click="approve"
              >
                {{ saving ? "Aguarde..." : "Aprovar" }}
              </b-button>
            </template>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import { getVueSelectErrorClass } from "@/helpers/validators";
import _ from "lodash";

export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BCol,
    BRow,
    BSpinner,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    callback: Function,
  },
  data() {
    return {
      loading: {
        form: false,
        certificateTypes: false,
        consultants: false,
      },
      consultantDisabled: true,
      consultant: undefined,
      certificateType: undefined,
      currentStatus: undefined,
      status: undefined,
      saving: false,
      isApproval: false,
    };
  },
  setup() {
    return {
      toast: useToast(),
      v$: useVuelidate(),
    };
  },
  validations() {
    return {
      consultant: { required },
      certificateType: { required },
    };
  },
  computed: {
    ...mapGetters({
      consultantCertificateSidebar: types.CONSULTANT_CERTIFICATE_SIDEBAR,
      certificateTypes: types.CONSULTANT_CERTIFICATE_TYPES,
      consultants: sharedTypes.CONSULTANTS,
    }),

    isPendingApproval() {
      return this.currentStatus === "pending";
    },

    certificateTypeOptions() {
      return this.certificateTypes || [];
    },

    isEditingCertificate() {
      return !!this.consultantCertificateSidebar.id;
    },
  },

  mounted() {
    this.loading.certificateTypes = true;
    this.getCertificateTypes()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text:
              "Ocorreu um erro ao carregar os tipos de certificado. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.certificateTypes = false;
      });

    this.loading.consultants = true;
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text:
              "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultants = false;
      });
  },
  methods: {
    ...mapMutations({
      mutateConsultantCertificateSidebar:
        types.MUTATE_CONSULTANT_CERTIFICATE_SIDEBAR,
    }),
    ...mapActions({
      saveConsultantCertificate: types.SAVE_CONSULTANT_CERTIFICATE,
      updateConsultantCertificate: types.UPDATE_CONSULTANT_CERTIFICATE,
      getCertificateTypes: types.GET_CONSULTANT_CERTIFICATE_TYPES,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getConsultantCertificate: types.GET_CONSULTANT_CERTIFICATE,
    }),

    onShow() {
      this.saving = false;
      this.status = undefined;
      this.isApproval = false;

      // Recarrega os tipos de curso para refletir edições recentes (ex.: nome alterado),
      // evitando que o dropdown mostre o nome antigo na aprovação.
      this.loading.certificateTypes = true;
      this.getCertificateTypes()
        .catch(() => {})
        .finally(() => {
          this.loading.certificateTypes = false;
        });

      if (this.consultantCertificateSidebar.consultant_id) {
        this.consultant = this.consultantCertificateSidebar.consultant_id;
        this.consultantDisabled = true;
      } else {
        this.consultant = undefined;
        this.consultantDisabled = false;
      }

      if (this.consultantCertificateSidebar.id) {
        this.loading.form = true;
        this.getConsultantCertificate(this.consultantCertificateSidebar.id)
          .then((resp) => {
            const data = resp.data;
            if (data.status !== "pending") {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Oops!",
                  text:
                    "Não é possível editar uma solicitação de certificado que já foi analisada anteriormente.",
                  icon: "AlertTriangleIcon",
                  variant: "warning",
                },
              });
              this.mutateConsultantCertificateSidebar({ visible: false });
              return;
            }
            this.consultant = Number(data.consultant_id);
            this.consultantDisabled = true;
            this.certificateType = Number(data.certificate_type_id);
            this.currentStatus = data.status;
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text:
                  "Ocorreu um erro ao carregar os dados da solicitação de certificado. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          })
          .finally(() => {
            this.loading.form = false;
          });
      } else {
        this.currentStatus = undefined;
        this.certificateType = undefined;
      }
    },

    approve() {
      this.isApproval = true;
      this.status = "approved";
      this.onSubmit();
    },

    reject() {
      this.isApproval = false;
      this.status = "rejected";
      this.onSubmit();
    },

    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;

      this.saving = true;

      const payload = {
        id: this.consultantCertificateSidebar.id,
        consultant_id: this.consultant,
        certificate_type_id: this.certificateType,
        status: this.status,
      };

      const saveFn = this.status ? this.updateConsultantCertificate : this.saveConsultantCertificate;

      saveFn(payload)
        .then(() => {
          let message =
            "Solicitação de certificado salva e enviada para análise com sucesso!";
          if (this.status === "approved") {
            message = "Solicitação de certificado aprovada com sucesso!";
          } else if (this.status === "rejected") {
            message = "Solicitação de certificado rejeitada com sucesso!";
          }

          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: message,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          this.mutateConsultantCertificateSidebar({ visible: false });

          if (this.consultantCertificateSidebar.saveAction) {
            this.consultantCertificateSidebar.saveAction();
          }
        })
        .catch((err) => {
          if (err?.response?.status === 400) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: err.response.data.error,
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            });
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text:
                  "Ocorreu um erro ao salvar a solicitação de certificado. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },

    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },

    clear() {
      this.consultant = undefined;
      this.consultantDisabled = true;
      this.certificateType = undefined;
      this.status = undefined;
      this.currentStatus = undefined;
      this.isApproval = false;

      this.mutateConsultantCertificateSidebar({
        id: undefined,
        consultant_id: undefined,
      });

      this.v$.$reset();
    },
    resetForm() {
      this.mutateConsultantCertificateSidebar({ visible: false });
      this.clear();
    },
  },
};
</script>

<style lang="scss">
.certificate-buttons {
  width: 11rem;
}
</style>
